import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Menu, User, Settings, Lock, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { ProfileSettingsDialog } from '@/components/layout/ProfileSettingsDialog';
import { ChangePasswordDialog } from '@/components/layout/ChangePasswordDialog';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { getApiEndpoint } from '@/utils/ERPUtils';
import site_logo from '@/assets/site_logo.jpg';
import { getCachedBrandLogo, setCachedBrandLogo } from '@/utils/branding';

import { logger } from '@/utils/logger';
interface MobileHeaderProps {
  onMenuClick: () => void;
  centerContent?: React.ReactNode;
}

export const MobileHeader: React.FC<MobileHeaderProps> = ({
  onMenuClick,
  centerContent
}) => {
  const [showProfileSettings, setShowProfileSettings] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
  const [brandLogo, setBrandLogo] = useState<string | null>(getCachedBrandLogo);
  const [logoLoaded, setLogoLoaded] = useState(false);

  useEffect(() => {
    let mounted = true;
    axios
      .get(getApiEndpoint('GET_DEFAULT_BRANDING'))
      .then((res) => {
        if (!mounted) return;
        const data = res?.data?.message?.data;
        // Fall back to the bundled logo when no company logo is set.
        const url = data?.logo_url || null;
        setBrandLogo(url);
        setCachedBrandLogo(url); // keep the reload cache in sync
      })
      .catch((err) => {
        logger.error('Branding fetch failed:', err);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const userRole = Array.isArray(user?.roles) ? user.roles[0] : (user?.roles || 'User');

  // Get user info
  const getAccountDisplayName = () => {
    return user?.name || user?.full_name || 'User';
  };

  const getAccountInitials = () => {
    const name = getAccountDisplayName();
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const performSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      logger.error('Logout error:', error);
      navigate('/login');
    }
  };
  const handleSignOut = () => setShowSignOutConfirm(true);

  return (
    <>
      <div
        className="flex items-center justify-between px-4 bg-brand-cream"
        style={{
          height: '64px',
          paddingTop: 'env(safe-area-inset-top, 0px)'
        }}
      >
        {/* Left - Menu Button */}
        <Button
        variant="ghost"
        size="icon"
        onClick={onMenuClick}
        className="text-f-dark hover:bg-black/10 p-2 h-10 w-10"
        >
        <Menu className="h-6 w-6" strokeWidth={2} />
        </Button>

        {/* Center - Custom content or Logo */}
       <div className="flex items-center justify-center flex-1 min-w-0">
        {centerContent ? centerContent : (
          <div className="dv3-brand-row">
            <img
              src={brandLogo || site_logo}
              alt="Brand logo"
              style={{
                height: 44,
                width: 'auto',
                mixBlendMode: 'multiply',
                opacity: logoLoaded ? 1 : 0,
                transition: 'opacity 200ms ease',
              }}
              onLoad={() => setLogoLoaded(true)}
              onError={(e) => {
                const img = e.currentTarget as HTMLImageElement;
                if (img.src !== site_logo && !img.src.endsWith(site_logo)) {
                  img.src = site_logo;
                } else {
                  img.style.display = 'none';
                }
              }}
            />
          </div>
        )}
      </div>

        {/* Right - User Avatar */}
        <div className="flex items-center space-x-1">
          {/* User Avatar Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="text-white hover:bg-white/10 active:bg-white/20 min-w-[44px] min-h-[44px] p-1"
              >
                {user?.avatar ? (
                  <div className="w-9 h-9 rounded-full overflow-hidden border-2 border-f-dark/30">
                    <img
                      src={user.avatar}
                      alt={getAccountDisplayName()}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        // Fall back to initials on image load error
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.parentElement!.innerHTML = `<div class="w-full h-full bg-f-dark flex items-center justify-center"><span class="text-sm font-semibold text-white">${getAccountInitials()}</span></div>`;
                      }}
                    />
                  </div>
                ) : (
                  <div className="w-9 h-9 rounded-full bg-f-dark flex items-center justify-center">
                    <span className="text-sm font-semibold text-white">{getAccountInitials()}</span>
                  </div>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="dark w-56">
              <DropdownMenuLabel>{getAccountDisplayName()}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setShowProfileSettings(true)}>
                <Settings className="h-4 w-4 mr-2" />
                Profile Settings
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShowChangePassword(true)}>
                <Lock className="h-4 w-4 mr-2" />
                Change Password
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleSignOut} className="text-destructive">
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Profile Settings Dialog */}
      <ProfileSettingsDialog open={showProfileSettings} onOpenChange={setShowProfileSettings} />

      {/* Change Password Dialog */}
      <ChangePasswordDialog open={showChangePassword} onOpenChange={setShowChangePassword} />

      {/* Sign Out Confirmation */}
      <ConfirmationDialog
        open={showSignOutConfirm}
        onOpenChange={setShowSignOutConfirm}
        contentClassName="dark bg-background text-foreground"
        onConfirm={performSignOut}
        title="Sign out?"
        description="You'll need to sign in again to access your account."
        confirmText="Sign Out"
        cancelText="Cancel"
        confirmVariant="destructive"
      />
    </>
  );
};